// ============================================================
// FEATURE: DeadChannel — a haunted retro CRT TELEVISION set.
// Derived from Ambient/VhsOverlay (scanlines/flicker/tracking) +
// CrtTerminal (curved phosphor screen shell).
//
// A channel knob cycles a tiny playlist:
//   1) snow / static  2-3) artportfolio feeds behind scanlines
//   4) SMPTE-style NO SIGNAL color bars  5) a slow text crawl.
// A power button toggles a CRT power-off white-dot collapse.
//
// PERF: the static "snow" is a CHEAP CSS keyframe (animated
// background-position over a small tiled noise gradient) — NO
// per-pixel canvas loop. Only the ACTIVE channel renders. All
// animation is paused (animation-play-state) when the set is
// powered off OR scrolled off-screen / tab hidden, gated by the
// shared whenVisible() observer. No rAF, no timers, no canvases.
//
// Non-draggable content component — mount as the artComp of a
// WindowsCard to inherit the draggable titlebar + minimize.
// ============================================================

import * as React from "react";
import './deadchannel.css';
import data from "./channels.json";

// Reuse ONLY existing assets. ../../../images from this file's depth.
// De-duplicated from the MAIN board: waitingRoom lives on the main surface, so
// this feed pulls deepHeavy (not on the main board) instead. skeyeSkape is an
// eye/theme piece and may recur by design. -web variant dodges oversized texture.
import skeyeSkape from "../../../images/artportfolio/skeyeSkape-web.png";
import deepHeavy from "../../../images/artportfolio/deepHeavy-web.png";

import { whenVisible, prefersReducedMotion } from '../_shared/sketchFrame';

// Map the json image keys to the statically-imported (bundled) URLs.
const IMAGE_MAP = {
    skeyeSkape: skeyeSkape,
    deepHeavy: deepHeavy,
};

const CHANNELS = data.channels;
const CRAWL_TEXT = data.crawl.join("   ◆◆◆   ");

// SMPTE-ish bar palette (top row), reused by the css gradient via inline style.
const SMPTE_BARS = [
    "#c0c0c0", "#c0c000", "#00c0c0", "#00c000",
    "#c000c0", "#c00000", "#0000c0",
];

class DeadChannel extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            index: 0,        // current channel index into CHANNELS
            on: true,        // power state
            powering: false, // true mid power-off collapse animation
            paused: false,   // gated off-screen / tab hidden (animation-play-state)
        };

        // Root element for the visibility gate.
        this.rootRef = React.createRef();
        this._cleanupFrame = null;
        this._powerTimer = null; // handle for the power-off collapse timeout

        this.channelUp = this.channelUp.bind(this);
        this.channelDown = this.channelDown.bind(this);
        this.togglePower = this.togglePower.bind(this);

        this._reduced = prefersReducedMotion();
    }

    componentDidMount() {
        // Pause every CSS animation when the TV scrolls off-screen OR the tab is
        // hidden; resume when it returns AND the tab is visible. No rAF loop here
        // — we only flip a boolean that drives animation-play-state on the CSS.
        this._cleanupFrame = whenVisible(
            this.rootRef.current,
            () => this.setState({ paused: false }),
            () => this.setState({ paused: true })
        );
    }

    componentWillUnmount() {
        if (this._cleanupFrame) {
            this._cleanupFrame();
            this._cleanupFrame = null;
        }
        // Cancel any in-flight power-off collapse timeout so it never fires
        // setState() on an unmounted component.
        if (this._powerTimer != null) {
            window.clearTimeout(this._powerTimer);
            this._powerTimer = null;
        }
    }

    channelUp() {
        if (!this.state.on) return;
        this.setState((s) => ({ index: (s.index + 1) % CHANNELS.length }));
    }

    channelDown() {
        if (!this.state.on) return;
        this.setState((s) => ({
            index: (s.index - 1 + CHANNELS.length) % CHANNELS.length,
        }));
    }

    togglePower() {
        // Any prior power-off collapse is now stale (rapid toggling) — drop it
        // so a queued timeout can't force the set back off after we turn it on.
        if (this._powerTimer != null) {
            window.clearTimeout(this._powerTimer);
            this._powerTimer = null;
        }

        if (this.state.on) {
            // Powering OFF: play the white-dot collapse, then go dark.
            // Reduced-motion users skip straight to off (no collapse flash).
            if (this._reduced) {
                this.setState({ on: false, powering: false });
                return;
            }
            this.setState({ powering: true });
            // The collapse is a pure CSS animation; we just flip state after it.
            // A single short timeout (not a loop) sequences off — no rAF/interval.
            this._powerTimer = window.setTimeout(() => {
                this._powerTimer = null;
                this.setState({ on: false, powering: false });
            }, 420);
        } else {
            this.setState({ on: true, powering: false });
        }
    }

    renderActiveChannel() {
        const chan = CHANNELS[this.state.index];

        switch (chan.kind) {
            case "image": {
                const src = IMAGE_MAP[chan.image];
                return (
                    <div className="dc-feed dc-feed-image">
                        <img className="dc-feed-img" src={src} alt="" draggable={false} />
                        {/* cheap CSS rolling-snow haze layered over the feed */}
                        <div className="dc-snow dc-snow-faint" />
                    </div>
                );
            }
            case "bars": {
                const stops = SMPTE_BARS
                    .map((c, i) => {
                        const a = (i * 100) / SMPTE_BARS.length;
                        const b = ((i + 1) * 100) / SMPTE_BARS.length;
                        return `${c} ${a}%, ${c} ${b}%`;
                    })
                    .join(", ");
                return (
                    <div className="dc-feed dc-feed-bars">
                        <div
                            className="dc-bars"
                            style={{ background: `linear-gradient(90deg, ${stops})` }}
                        />
                        <div className="dc-bars-bottom" />
                        <div className="dc-nosignal">NO SIGNAL</div>
                    </div>
                );
            }
            case "crawl": {
                return (
                    <div className="dc-feed dc-feed-crawl">
                        <div className="dc-crawl-head">{"▲ EMERGENCY BROADCAST ▲"}</div>
                        <div className="dc-crawl-window">
                            <div className="dc-crawl-text">{CRAWL_TEXT}</div>
                        </div>
                    </div>
                );
            }
            case "snow":
            default:
                return (
                    <div className="dc-feed dc-feed-snow">
                        <div className="dc-snow" />
                    </div>
                );
        }
    }

    render() {
        const { on, powering, paused, index } = this.state;
        const chan = CHANNELS[index];

        // One class flag drives every keyframe's play-state across the CSS tree.
        const rootClass =
            "dc-tv" +
            (on ? " dc-on" : " dc-off") +
            (powering ? " dc-powering" : "") +
            (paused ? " dc-paused" : "");

        return (
            <div className={rootClass} ref={this.rootRef}>
                {/* curved glass tube */}
                <div className="dc-bezel">
                    <div className="dc-screen">
                        {/* Render ONLY the active channel (powered on). */}
                        {on && (
                            <div className="dc-channel-layer">
                                {this.renderActiveChannel()}
                            </div>
                        )}

                        {/* overlays: scanlines + vignette + rolling tracking band */}
                        <div className="dc-scanlines" aria-hidden="true" />
                        <div className="dc-vignette" aria-hidden="true" />
                        {on && <div className="dc-tracking" aria-hidden="true" />}

                        {/* on-screen display (channel + label), VHS-blue */}
                        {on && (
                            <div className="dc-osd">
                                <span className="dc-osd-ch">CH {chan.ch}</span>
                                <span className="dc-osd-label">{chan.label}</span>
                            </div>
                        )}

                        {/* power-off white-dot collapse + dark void when off */}
                        {(powering || !on) && (
                            <div className="dc-poweroff" aria-hidden="true">
                                <span className="dc-dot" />
                            </div>
                        )}
                    </div>
                </div>

                {/* control deck: faux wood/metal panel with knobs + buttons */}
                <div className="dc-controls">
                    <button
                        type="button"
                        className="dc-power"
                        onClick={this.togglePower}
                        aria-pressed={on}
                        title="Power"
                    >
                        <span className="dc-power-led" />
                        <span className="dc-btn-cap">PWR</span>
                    </button>

                    <div className="dc-channel-knob">
                        <button
                            type="button"
                            className="dc-ch-btn dc-ch-down"
                            onClick={this.channelDown}
                            disabled={!on}
                            title="Channel down"
                        >
                            {"▼"}
                        </button>
                        <div className="dc-ch-readout">
                            <span className="dc-ch-num">{on ? chan.ch : "--"}</span>
                            <span className="dc-ch-word">CH</span>
                        </div>
                        <button
                            type="button"
                            className="dc-ch-btn dc-ch-up"
                            onClick={this.channelUp}
                            disabled={!on}
                            title="Channel up"
                        >
                            {"▲"}
                        </button>
                    </div>

                    <div className="dc-brand">AMBULA&middot;VISION</div>
                </div>
            </div>
        );
    }
}

export default DeadChannel;
